"use client";

import { createContext, useContext, useState, ReactNode, useMemo, useCallback, useRef, useEffect } from 'react';
import { Card } from '@/features/flashcards/types';

interface FlashcardsContextType {
    cards: Card[];
    set__cards: React.Dispatch<React.SetStateAction<Card[]>>;
}

const FlashcardsContext = createContext<FlashcardsContextType | undefined>(undefined);
const FlashcardsActionsContext = createContext<{
    set__cards: React.Dispatch<React.SetStateAction<Card[]>>;
    get_cards: () => Card[];
} | undefined>(undefined);

export function FlashcardsProvider({ children }: { children: ReactNode }) {
    const [cards, set__cards] = useState<Card[]>([]);
    const cards_ref = useRef<Card[]>(cards);

    useEffect(() => {
        cards_ref.current = cards;
    }, [cards]);

    const get_cards = useCallback(() => cards_ref.current, []);
    const actions = useMemo(() => ({ set__cards, get_cards }), [set__cards, get_cards]);

    return (
        <FlashcardsActionsContext.Provider value={actions}>
            <FlashcardsContext.Provider 
                value={{ 
                    cards, 
                    set__cards,
                }}
            >
                {children}
            </FlashcardsContext.Provider>
        </FlashcardsActionsContext.Provider>
    );
}

export function useFlashcards() {
    const context = useContext(FlashcardsContext);
    if (context === undefined) {
        throw new Error('useFlashcards must be used within a FlashcardsProvider');
    }
    return context;
}

export function useFlashcardsActions() {
    const context = useContext(FlashcardsActionsContext);
    if (context === undefined) {
        throw new Error('useFlashcardsActions must be used within a FlashcardsProvider');
    }
    return context;
}
